import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { Manager, Reference, Popper } from "react-popper";
import classnames from "classnames";
import { IconChevronUp16, IconClose16, IconChevronDown16 } from "../Icons/Icons";
import "./popup.scss";

class PopupManager extends PureComponent {
  constructor(props) {
    super(props);
    this.handleOnEscape = this.handleOnEscape.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.scheduleUpdate = null;
    this.popupInner = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("keydown", this.handleOnEscape, false);
    document.addEventListener("mousedown", this.handleClick, false);
  }

  componentDidUpdate(prevProps) {
    const { children } = this.props;
    if (prevProps.children !== children && this.scheduleUpdate) {
      this.scheduleUpdate();
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleOnEscape, false);
    document.removeEventListener("mousedown", this.handleClick, false);
  }

  handleOnEscape(e) {
    const { showPopup, closePopup } = this.props;
    if (showPopup && e.keyCode === 27) {
      closePopup();
    }
  }

  handleClick(e) {
    const { showPopup } = this.props;
    if (showPopup && this.popupInner.current && !this.popupInner.current.contains(e.target)) {
      const { closePopup } = this.props;
      setTimeout(closePopup, 200);
    }
  }

  render() {
    const {
      children,
      showPopup,
      handleTogglePopup,
      className,
      position,
      CustomTrigger,
      showCloseButton,
    } = this.props;
    const popupSettings = { preventOverflow: { padding: 24 } };
    return (
      <Manager>
        <Reference>
          {({ ref }) => {
            const triggerClasses = classnames("popup-trigger", className, {
              "popup-trigger--open": showPopup,
            });

            if (CustomTrigger) {
              return (
                <CustomTrigger
                  forwardedRef={ref}
                  className={triggerClasses}
                  onClick={handleTogglePopup}
                />
              );
            }

            const arrowIcon =
              position === "top" ? (
                <IconChevronUp16 className="popup-trigger__icon" />
              ) : (
                <IconChevronDown16 className="popup-trigger__icon" />
              );

            return (
              <button
                type="button"
                ref={ref}
                className={triggerClasses}
                onClick={handleTogglePopup}
                aria-label="Toggle"
              >
                {!showPopup ? arrowIcon : <IconClose16 className="popup-trigger__icon" />}
              </button>
            );
          }}
        </Reference>
        {showPopup && (
          <Popper placement={position} modifiers={popupSettings}>
            {({ ref, style, placement, arrowProps, scheduleUpdate }) => {
              this.scheduleUpdate = scheduleUpdate;

              const classes = classnames("popup", {
                "popup--has-close-button": showCloseButton,
              });

              return (
                <div ref={ref} className={classes} style={style} data-placement={placement}>
                  <div className="popup__inner" ref={this.popupInner}>
                    {showCloseButton && (
                      <button
                        className="popup__close-button"
                        onClick={handleTogglePopup}
                        aria-label="Close"
                      >
                        <IconClose16 className="popup-trigger__icon" />
                      </button>
                    )}
                    {children}
                    <div className="popup__arrow" ref={arrowProps.ref} style={arrowProps.style} />
                  </div>
                </div>
              );
            }}
          </Popper>
        )}
      </Manager>
    );
  }
}

PopupManager.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  showPopup: PropTypes.bool,
  handleTogglePopup: PropTypes.func.isRequired,
  closePopup: PropTypes.func.isRequired,
  className: PropTypes.string,
  position: PropTypes.oneOf(["top", "default"]),
  CustomTrigger: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  showCloseButton: PropTypes.bool,
};

PopupManager.defaultProps = {
  className: "",
};

export default PopupManager;
